import { useMutation } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useBlocker } from "react-router";

import { useOrganization } from "@/auth/organization";
import { Button, ErrorAlert, Form, Modal, useFormField } from "@/components/ui";
import { portal } from "@/lib/api";
import { useStoreProfile, useTrack } from "@/lib/growth";
import { primarySite, useOverview } from "@/lib/queries";

import type { StepProps } from "./index";
import { FieldLabel, inputClass, NavRow, PrimaryButton, StepHeading, StepTag } from "./ui";

type Answers = { business_name: string; services: string; audiences: string; website: string; goals: string };
const FIELDS = ["business_name", "services", "audiences", "website", "goals"] as const;
const LABELS: Answers = {
  business_name: "Business name",
  services: "Services",
  audiences: "Audiences",
  website: "Website",
  goals: "Goals",
};

/** "https://saltandbloom.com/" → "saltandbloom.com", as people type it. */
export const prettyUrl = (url: string) => url.replace(/^https?:\/\//, "").replace(/\/$/, "");

/**
 * Step 3 (Figma ONB-03, 4259:4826): the details Weavex interprets developments against. The name and
 * website start from what CortexGrip already knows; leaving with unsaved answers asks first (QA-22).
 */
export function ContextStep({ profile, niche, advance, goTo }: StepProps) {
  const { orgId, current } = useOrganization();
  const store = useStoreProfile();
  const track = useTrack();
  const site = primarySite(useOverview().data?.recent_sites ?? []);

  // What's saved, else what CortexGrip already knows (the website once the overview loads)…
  const known: Answers = {
    business_name: profile.business_name ?? current.organization.name,
    services: profile.services ?? "",
    audiences: profile.audiences ?? "",
    website: profile.website ? prettyUrl(profile.website) : (site?.hostname ?? ""),
    goals: profile.goals ?? "",
  };
  // …overlaid with what the person typed here.
  const [edits, setEdits] = useState<Partial<Answers>>({});
  const answers: Answers = { ...known, ...edits };
  const dirty = FIELDS.some((f) => edits[f] !== undefined && edits[f] !== known[f]);

  const leaving = useRef(false);
  const save = useMutation({
    mutationFn: () => portal.saveGrowthStep(orgId, 3, { ...answers, version: profile.version }),
    onSuccess: (next) => {
      leaving.current = true;
      store(next);
      track({ name: "onboarding_step_completed", properties: { step_id: "context" } });
      advance(4);
    },
  });

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && !leaving.current && currentLocation.pathname !== nextLocation.pathname,
  );
  const unsaved = FIELDS.filter((f) => edits[f] !== undefined && edits[f] !== known[f]).map((f) => LABELS[f]);

  const set = (field: keyof Answers) => (value: string) => setEdits((e) => ({ ...e, [field]: value }));
  const examples = niche?.placeholders;

  return (
    <Form
      className="flex flex-col gap-[22px]"
      error={save.error}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <StepTag step={3} />
      <StepHeading title="Tell us about your business">
        This shapes how Weavex interprets developments for your specific business — you can update it any time
        from Settings.
      </StepHeading>

      <TextField
        label="Business name"
        name="business_name"
        required
        value={answers.business_name}
        onChange={set("business_name")}
        maxLength={200}
        autoComplete="organization"
      />
      <TextField
        label="Services"
        name="services"
        required
        multiline
        value={answers.services}
        onChange={set("services")}
        placeholder={examples?.services}
        maxLength={1000}
      />
      <TextField
        label="Audiences"
        name="audiences"
        required
        multiline
        value={answers.audiences}
        onChange={set("audiences")}
        placeholder={examples?.audiences}
        maxLength={1000}
      />
      <TextField
        label="Website"
        name="website"
        value={answers.website}
        onChange={set("website")}
        maxLength={2048}
        inputMode="url"
        autoComplete="url"
      />
      <TextField
        label="Goals"
        name="goals"
        multiline
        tall
        value={answers.goals}
        onChange={set("goals")}
        placeholder={examples?.goals}
        maxLength={1000}
      />

      <ErrorAlert error={save.error} />

      <NavRow onBack={() => goTo(2)}>
        <PrimaryButton type="submit" loading={save.isPending}>
          Continue
        </PrimaryButton>
      </NavRow>

      <Modal
        open={blocker.state === "blocked"}
        onClose={() => blocker.reset?.()}
        title="Leave without saving?"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => blocker.reset?.()}>
              Stay on this step
            </Button>
            <Button type="button" variant="danger" onClick={() => blocker.proceed?.()}>
              Leave without saving
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg-muted">
          Your changes to {listFormat.format(unsaved)} aren’t saved yet. Leaving this step discards them.
        </p>
      </Modal>
    </Form>
  );
}

const listFormat = new Intl.ListFormat("en-GB", { type: "conjunction" });

function TextField({
  label,
  name,
  required,
  multiline,
  tall,
  value,
  onChange,
  ...props
}: {
  label: string;
  name: string;
  required?: boolean;
  multiline?: boolean;
  tall?: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  inputMode?: "url";
  autoComplete?: string;
}) {
  // The API's messages explain what's missing (shown by the Form), so the control is only aria-required.
  const field = useFormField({ label, name });
  const { id } = field;
  const error = field.message;
  const control = { ...field.control, value, "aria-required": required, ...props };
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel htmlFor={id} required={required}>
        {label}
      </FieldLabel>
      {multiline ? (
        <textarea
          {...control}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputClass} resize-none leading-[1.8] drop-shadow-[0px_2px_4px_rgba(112,71,46,0.06)] ${tall ? "h-[78px]" : "h-16"}`}
        />
      ) : (
        <input {...control} onChange={(e) => onChange(e.target.value)} className={inputClass} />
      )}
      {/* the Form summary announces it; here it describes the field */}
      {error && (
        <p id={field.messageId} className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
